import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/AppHeader";
import { FloorPlan2D } from "@/components/FloorPlan2D";
import { api, downloadFile, errMsg } from "@/lib/api";
import { floorView } from "@/lib/walls";

const ApproveForm = ({ token, onDone }) => {
  const [f, setF] = useState({ name: "", email: "", note: "" });
  const [ok, setOk] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!ok) { toast.error("Tick the box to confirm your approval"); return; }
    setBusy(true);
    try { await api.post(`/share/${token}/approve`, f); toast.success("Thank you — your approval was sent"); onDone(); } catch (er) { toast.error(errMsg(er)); }
    setBusy(false);
  };
  const input = "w-full bg-[var(--card)] border border-[var(--line)] focus:border-[var(--cyan)] outline-none px-3 py-2.5 text-sm transition-colors";
  return (
    <form onSubmit={submit} noValidate className="space-y-3" data-testid="approve-form">
      <input data-testid="approve-name-input" className={input} placeholder="Your full name" value={f.name} onChange={set("name")} />
      <input data-testid="approve-email-input" className={input} placeholder="Email" type="email" value={f.email} onChange={set("email")} />
      <textarea data-testid="approve-note-input" className={input} rows={3} placeholder="Notes for your designer (optional)" value={f.note} onChange={set("note")} />
      <label className="flex items-start gap-2 text-xs text-[var(--muted)]">
        <input data-testid="approve-confirm-checkbox" type="checkbox" checked={ok} onChange={(e) => setOk(e.target.checked)} className="mt-0.5 accent-[var(--cyan)]" />
        I approve this layout and understand it is a schematic preview, not a construction drawing.
      </label>
      <button data-testid="approve-submit-btn" disabled={busy} className="w-full py-3 bg-[var(--cyan)] text-[var(--bg)] font-semibold disabled:opacity-50 flex items-center justify-center gap-2">
        {busy && <Loader2 size={15} className="animate-spin" />} Approve layout
      </button>
    </form>
  );
};

export default function SharePage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [floor, setFloor] = useState(0);
  const load = () => api.get(`/share/${token}`).then(({ data: d }) => setData(d)).catch((e) => setErr(errMsg(e)));
  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (data) document.title = `${data.title} · Layout preview`; }, [data]);
  if (err) return <div className="min-h-screen grid place-items-center text-red-400" data-testid="share-error">{err}</div>;
  if (!data) return <div className="min-h-screen grid place-items-center"><Loader2 className="animate-spin text-[var(--cyan)]" /></div>;
  const plan = data.plan, ap = data.approval;
  return (
    <div className="min-h-screen blueprint-grid" data-testid="share-page">
      <header className="h-16 px-6 lg:px-12 flex items-center justify-between border-b border-[var(--line)] glass"><Logo /><span className="font-mono text-[10px] uppercase tracking-widest text-[var(--amber)]">Client preview</span></header>
      <main className="max-w-6xl mx-auto px-6 py-12 grid lg:grid-cols-[1.6fr_1fr] gap-10">
        <section className="min-w-0">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[var(--cyan)]">Prepared by {data.prepared_by}</p>
          <h1 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight" data-testid="share-title">{data.title}</h1>
          {plan.floors.length > 1 && (
            <div className="mt-6 flex gap-1">{plan.floors.map((f, i) => (
              <button key={i} data-testid={`share-floor-${i}`} onClick={() => setFloor(i)} className={`px-3 py-1.5 font-mono text-xs border ${i === floor ? "border-[var(--cyan)] text-[var(--cyan)]" : "border-[var(--line)] text-[var(--muted)]"}`}>{f.name}</button>
            ))}</div>
          )}
          <div className="mt-6 aspect-[16/11] border border-[var(--line)] bg-[var(--surface)]" data-testid="share-floorplan">
            <FloorPlan2D key={floor} plan={floorView(plan, floor)} hideDims animate layers={{ furniture: true }} />
          </div>
          <div className="mt-6 flex flex-wrap gap-2" data-testid="share-rooms">
            {plan.floors[floor].rooms.map((r) => <span key={r.id} className="px-2.5 py-1 text-xs border border-[var(--line)] text-[var(--muted)]">{r.name}</span>)}
          </div>
        </section>
        <aside className="space-y-5">
          <div className="border border-[var(--line)] bg-[var(--surface)] p-6">
            {ap ? (
              <div data-testid="share-approved"><CheckCircle2 className="text-[var(--emerald)]" /><h3 className="mt-3 font-display font-semibold text-lg">Layout approved</h3>
                <p className="mt-1 text-sm text-[var(--muted)]">Approved by {ap.name} on {new Date(ap.at).toLocaleDateString()}. Your designer has been notified.</p></div>
            ) : (
              <><h3 className="font-display font-semibold text-lg">Approve this layout</h3>
                <p className="mt-1 mb-5 text-sm text-[var(--muted)]">Happy with the room arrangement? Approve it and your designer will prepare the full dimensioned drawing set.</p>
                <ApproveForm token={token} onDone={load} /></>
            )}
          </div>
          <button data-testid="share-preview-pdf-btn" onClick={() => downloadFile(`/share/${token}/preview-pdf`, `${data.title.replace(/[^\w-]+/g, "_")}-preview.pdf`).catch((e) => toast.error(errMsg(e)))}
            className="w-full py-2.5 text-sm border border-[var(--line)] hover:border-[var(--cyan)] transition-colors flex items-center justify-center gap-2"><FileText size={14} /> Download preview PDF</button>
          <p className="text-[11px] text-[var(--muted)]">Schematic preview — no dimensions, not to scale.</p>
        </aside>
      </main>
    </div>
  );
}
